export type DataLevel = 'L1' | 'L2' | 'L3' | 'L4'
export type FieldType = 'money' | 'percent' | 'id' | 'email' | 'token' | 'card' | 'default'
export type DisplayMode = 'show' | 'mask' | 'locked'
export type AdRole = 'viewer' | 'optimizer' | 'lead' | 'admin'

export interface FilterResult {
  display: DisplayMode
  value: string | null
  hint?: string
}

/**
 * 四级脱敏过滤
 *
 * @param value      原始格式化后的字符串（如 "$4,280"）
 * @param level      字段所需数据等级 L1-L4
 * @param userLevel  当前用户数据等级（1=viewer 2=optimizer 3=lead 4=admin），直接取 adContext.data_level
 * @param fieldType  字段类型，决定脱敏格式
 * @param alwaysLock 差一级也锁定，不走脱敏中间态（毛利率/盈亏CPA/产品成本/经验库）
 */
export function securityFilter(
  value: string,
  level: DataLevel,
  userLevel: number,
  fieldType: FieldType = 'default',
  alwaysLock = false,
): FilterResult {
  const dataLevelNum = parseInt(level[1])
  const userLevelNum = userLevel

  // 有权限
  if (dataLevelNum <= userLevelNum) {
    // L4 即使 admin 也脱敏凭证类字段
    if (level === 'L4' && fieldType === 'token') {
      return { display: 'mask', value: maskValue(value, 'token') }
    }
    return { display: 'show', value }
  }

  // 无权限 + alwaysLock → 直接锁定，不走脱敏
  if (alwaysLock) {
    return {
      display: 'locked',
      value: null,
      hint: `需要 ${levelRoleName(dataLevelNum)} 及以上权限查看`,
    }
  }

  // 差一级：脱敏显示
  if (dataLevelNum === userLevelNum + 1) {
    return {
      display: 'mask',
      value: maskValue(value, fieldType),
      hint: `需要 ${levelRoleName(dataLevelNum)} 及以上权限查看`,
    }
  }

  // 差两级以上：锁定
  return {
    display: 'locked',
    value: null,
    hint: `需要 ${levelRoleName(dataLevelNum)} 及以上权限查看`,
  }
}

function maskValue(value: string, type: FieldType): string {
  switch (type) {
    case 'money':   return '$*'
    case 'percent': return '**.**%'
    case 'id':      return '***...***'
    case 'email':   return '***@***.com'
    case 'token':   return value.slice(0, 5) + '***...' + value.slice(-4)
    case 'card':    return 'Visa ...' + value.slice(-4)
    default:        return '****'
  }
}

export function levelRoleName(level: number): string {
  return ['', 'Viewer', 'Optimizer', 'Lead', 'Admin'][level] ?? 'Admin'
}

/** 从 GateKeeper permissionCodes 列表推导当前用户的广告角色 */
export function deriveAdRole(permissionCodes: string[]): AdRole {
  if (permissionCodes.includes('ad:role:admin'))     return 'admin'
  if (permissionCodes.includes('ad:role:lead'))      return 'lead'
  if (permissionCodes.includes('ad:role:optimizer')) return 'optimizer'
  return 'viewer'
}
